import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Re-use the same worker version already bundled for the flipbook viewer
pdfjsLib.GlobalWorkerOptions.workerSrc =
  '//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

/**
 * Renders the first page of a PDF as a lazy-loaded cover thumbnail.
 *
 * Behaviour:
 *  - Stays idle until the card scrolls near the viewport (IntersectionObserver).
 *  - Shows a subtle spinner while the PDF is loading.
 *  - Fades in the cover image once rendered.
 *  - Returns null on any error so the parent's fallback (gradient + icon) shows through.
 */
const PdfCoverThumbnail = ({ pdfUrl }) => {
  const wrapperRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error

  // ── Step 1: trigger load when the card approaches the viewport ──────────────
  useEffect(() => {
    if (!pdfUrl) { setStatus('error'); return; }

    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setStatus('loading');
        }
      },
      { rootMargin: '300px' } // pre-load 300 px before entering the viewport
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [pdfUrl]);

  // ── Step 2: render page 1 to an off-screen canvas, then convert to data URL ─
  useEffect(() => {
    if (status !== 'loading' || !pdfUrl) return;

    let cancelled = false;

    (async () => {
      try {
        const url = `${process.env.PUBLIC_URL || ''}${pdfUrl}`;
        const pdf = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        // Scale so the rendered height ≈ 560 px — sharp even at 2× DPI for card thumbnails
        const nativeVp = page.getViewport({ scale: 1 });
        const scale = 560 / nativeVp.height;
        const vp = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(vp.width);
        canvas.height = Math.round(vp.height);

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: vp,
        }).promise;

        if (!cancelled) {
          setDataUrl(canvas.toDataURL('image/jpeg', 0.88));
          setStatus('ready');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [status, pdfUrl]);

  // On error the component unmounts — the parent gradient + book icon is visible
  if (status === 'error') return null;

  return (
    <div ref={wrapperRef} className="absolute inset-0">
      {/* Spinner while loading */}
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="block w-7 h-7 rounded-full border-2 border-white/30 border-t-white/80 animate-spin"
            aria-label="Cargando portada"
          />
        </div>
      )}

      {/* Cover image — fades in once the canvas is ready */}
      {dataUrl && (
        <img
          src={dataUrl}
          alt="Portada de la publicación"
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: 'contain',
            opacity: status === 'ready' ? 1 : 0,
            transition: 'opacity 0.35s ease',
          }}
        />
      )}
    </div>
  );
};

export default PdfCoverThumbnail;
